import type { FormEvent, ReactNode } from 'react';
import Drawer from '@mui/material/Drawer';
import CircularProgress from '@mui/material/CircularProgress';
import { Close } from '@mui/icons-material';
import styled from '@emotion/styled';
import { uiTheme as t } from './theme';
import Button from './Button';

/** Ancho del panel: `md` para un formulario de campos, `lg` cuando lleva tabla. */
export type FormDrawerWidth = 'md' | 'lg';

export interface FormDrawerLabels {
  close: string;
  cancel: string;
  /** Sin etiqueta de envío no se pinta el botón: el cajón es de solo lectura. */
  submit?: string;
}

export interface FormDrawerProps {
  open: boolean;
  /**
   * Identificador del `<form>`. El botón de guardar vive en el pie, fuera del
   * formulario, y lo envía con `form={formId}`.
   */
  formId: string;
  title: string;
  subtitle?: string;
  width?: FormDrawerWidth;
  labels: FormDrawerLabels;
  /** Bloquea el pie y el cierre mientras el guardado está en vuelo. */
  submitting?: boolean;
  /** Deshabilita el envío sin ocultarlo, para cuando el formulario no es válido. */
  canSubmit?: boolean;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
}

const WIDTH: Record<FormDrawerWidth, number> = { md: 480, lg: 720 };

const Panel = styled.div<{ width: FormDrawerWidth }>(({ width }) => ({
  width: WIDTH[width],
  maxWidth: '92vw',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  [`@media (max-width: ${t.breakpoint.sm})`]: {
    width: '100vw',
    maxWidth: '100vw',
  },
}));

const Head = styled.div({
  flex: 'none',
  padding: '22px 26px',
  borderBottom: `1px solid ${t.color.rowBorder}`,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
});

const HeadTitle = styled.div({
  fontFamily: t.font,
  fontSize: 18,
  fontWeight: 600,
  color: t.color.title,
});

const HeadSubtitle = styled.div({
  fontFamily: t.font,
  fontSize: 13,
  color: t.color.muted,
  marginTop: 3,
});

const CloseButton = styled.button({
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: t.color.muted,
  display: 'inline-flex',
  '& .MuiSvgIcon-root': { fontSize: 24 },
});

const Body = styled.form({
  flex: 1,
  overflowY: 'auto',
  padding: '24px 26px',
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
});

const Footer = styled.div({
  flex: 'none',
  borderTop: `1px solid ${t.color.rowBorder}`,
  padding: '18px 26px',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 12,
});

/**
 * Cajón lateral derecho: el patrón de formularios de la aplicación.
 *
 * Trae la cáscara y el pie. El pie va aquí y no en cada consumidor porque es lo
 * primero que diverge cuando se copia: dos cajones idénticos acaban con dos
 * separaciones distintas entre los botones.
 *
 * El contenido va dentro de un `<form id={formId}>`, y el botón de guardar lo
 * envía desde fuera con `form={formId}`. Así el consumidor conserva el envío
 * nativo, con su validación y su `preventDefault`.
 */
const FormDrawer = ({
  open,
  formId,
  title,
  subtitle,
  width = 'md',
  labels,
  submitting = false,
  canSubmit = true,
  onClose,
  onSubmit,
  children,
}: FormDrawerProps) => {
  // Ni el botón, ni la tecla de escape, ni el fondo cierran a media escritura.
  const requestClose = () => {
    if (!submitting) onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={requestClose}
      PaperProps={{ sx: { boxShadow: '-8px 0 30px rgba(16, 24, 40, 0.18)' } }}
    >
      <Panel width={width}>
        <Head>
          <div>
            <HeadTitle>{title}</HeadTitle>
            {subtitle ? <HeadSubtitle>{subtitle}</HeadSubtitle> : null}
          </div>
          <CloseButton type="button" onClick={requestClose} aria-label={labels.close}>
            <Close />
          </CloseButton>
        </Head>

        <Body id={formId} onSubmit={onSubmit}>
          {children}
        </Body>

        <Footer>
          <Button type="button" variant="secondary" onClick={requestClose} disabled={submitting}>
            {labels.cancel}
          </Button>
          {labels.submit ? (
            <Button type="submit" form={formId} variant="primary" disabled={submitting || !canSubmit}>
              {submitting ? <CircularProgress size={16} /> : null}
              {labels.submit}
            </Button>
          ) : null}
        </Footer>
      </Panel>
    </Drawer>
  );
};

export default FormDrawer;
